import { guardarAutorizacion } from '../guardarAutorizacion';
// src/components/Authorizations/EditAuthorizationForm/EditAuthorizationForm.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Input, message, Typography, Form, Row, Col } from "antd";
import api from "../../../../axiosConfig";
import dayjs from "../../../../utils/dayjs";
import { useUser } from "../../../../UserContext";
import {
  UserAddOutlined,
  TeamOutlined,
  SafetyOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

// Componentes hijos
import PatientInfo from "../Form/PatientInfo";
import ResponsibleInfo from "../Form/ResponsibleInfo";
import AuthorizationData from "../Form/AuthorizationData";
import AuthorizationDates from "../Form/AuthorizationDates";
import GeografiaAutorizacion from "../Form/GeografiaAutorizacion";

const { TextArea } = Input;
const { Title } = Typography;

// ------------------
// Códigos especiales
// ------------------
const SPECIAL_CUPS_CODES = ["FS33303", "FS33305"];
const INTERMUNICIPAL_CODES = [
  "FS31324",
  "FS31325",
  "FS31326",
  "FS31327",
  "FS31355",
  "FS31356",
  "FS31357",
  "FS31358",
];

// ------------------
// Estado inicial del formulario
// ------------------
const initialFormState = {
  paciente_id: "",
  nombre_paciente: "",
  tipo_documento_paciente: "",
  numero_documento_paciente: "",
  genero: "",
  departamento: "",
  ciudad: "", // ciudad del paciente
  empresa: "",
  regimen: "",
  observaciones: "",
  nombre_responsable: "",
  parentesco_responsable: "",
  telefono_responsable: "",
  codigo_mipres: "",
  cups_codigo: "",
  cups_descripcion: "",
  tipo_viaje: "",
  tipo_traslado: "",
  numero_entrega: 1,
  entregas_autorizadas: 1,
  numero_autorizacion: "",
  numero_pre_autorizacion: "",
  proposito_id: "",
  proposito: "",
  tipo_autorizacion: "abierta",
  inicio_fecha_autorizacion: null,
  final_fecha_autorizacion: null,
  estado_autorizacion: "Activa",
  categoria_autorizacion: "",
  total_traslados_asignados: "",
  kilometros_autorizados: "",
  kilometros_por_servicios: "",
  copago: 0,
  porcentaje_copago_selected: 0,
  copago_diario: 0,
  valor_maximo_copago: null,
  // ORIGEN
  origen_institucion: "",
  origen_coordenadas: null,
  origen_departamento: "",
  origen_ciudad: "",
  origen_localidad: "",
  origen_barrio: "",
  origen_vereda_corregimiento: "",
  origen_direccion: "",
  origen_indicaciones: "",
  // DESTINO
  destino_institucion: "",
  destino_coordenadas: null,
  destino_departamento: "",
  destino_ciudad: "",
  destino_localidad: "",
  destino_barrio: "",
  destino_vereda_corregimiento: "",
  destino_direccion: "",
  destino_indicaciones: "",
  // Otros
  no_pbs: true,
  calendario: [],
  crear_nuevo_mipres: false,
  // Campos TAI
  es_tai: false,
  tai_codigo: [],
  coberturas: "",
  transportadora_id: null,
  cliente_tai_id: null,
  tai_tipo_transporte: null,
  tai_configuracion: null,
  con_acompanante: false,  // Solo para TAI Intermunicipal
  tiene_intermunicipal: false, // True si algún CUPS seleccionado es Intermunicipal
};

// ------------------
// Funciones puras (helpers)
// ------------------

// (1) Convertir NOMBRE -> ID
function findDepartamentoIdByName(name, departamentos) {
  if (!name || !Array.isArray(departamentos)) return "";
  const dep = departamentos.find((d) => d.nombre === name);
  return dep ? dep.id : "";
}
function findInstitucionIdByName(name, instituciones) {
  if (!name || !Array.isArray(instituciones)) return "";
  const inst = instituciones.find((i) => i.nombre === name);
  return inst ? inst.id : "";
}

// (2) Convertir ID -> NOMBRE
function getDepartmentName(id, departamentos) {
  if (!id || !Array.isArray(departamentos)) return "";
  const dep = departamentos.find((d) => d.id === id);
  return dep ? dep.nombre : "";
}
function getCityName(cityId, ciudades) {
  if (!cityId || !Array.isArray(ciudades)) return "";
  const c = ciudades.find((ciudad) => ciudad.id === cityId);
  return c ? c.nombre : "";
}
function getInstitutionName(instId, instituciones) {
  if (!instId || !Array.isArray(instituciones)) return "";
  const i = instituciones.find((inst) => inst.id === instId);
  return i ? i.nombre : "";
}

// (3) Parsear coordenadas: de objeto a string "lat, lng" (formato user-friendly)
function parsePoint(geom) {
  if (!geom || !geom.coordinates) return "";
  const [lng, lat] = geom.coordinates;
  return `${lat}, ${lng}`;
}

// (4) Obtener datos de paciente (para origen)
function getDatosOrigenPaciente(patient) {
  const coords = patient.coordenadas?.coordinates || [];
  return {
    origen_departamento: patient.departamento_id,
    origen_ciudad: patient.ciudad_id,
    origen_localidad: patient.localidad || "",
    origen_barrio: patient.barrio || "",
    origen_vereda_corregimiento: patient.vereda_corregimiento || "",
    origen_direccion: patient.direccion || "",
    origen_indicaciones: patient.indicaciones || "",
    origen_coordenadas: coords.length ? `${coords[0]}, ${coords[1]}` : "",
  };
}
function getBasicPatientData(patient) {
  return {
    paciente_id: patient.id,
    nombre_paciente: patient.nombre,
    tipo_documento_paciente: patient.TipoDocumento?.descripcion || "",
    numero_documento_paciente: patient.numero_documento || "",
    genero: patient.genero || "",
  };
}
function getAdditionalPatientData(patient) {
  return {
    departamento: patient.Ciudad?.Departamento?.nombre || "",
    ciudad: patient.Ciudad?.nombre || "", // Ciudad del paciente
    empresa: patient.Cliente?.nombre_cliente || "",
    regimen: patient.Regimen?.nombre || "",
    nombre_responsable: patient.nombre_responsable || "",
    parentesco_responsable: patient.parentesco_responsable || "",
    telefono_responsable: patient.telefono_responsable || "",
  };
}
function getPatientFormData(patient) {
  const origen = getDatosOrigenPaciente(patient);
  return {
    ...getBasicPatientData(patient),
    ...getAdditionalPatientData(patient),
    // Origen
    origen_departamento: patient.departamento_id || "",
    origen_ciudad: patient.ciudad_id || "", // Si no se cambia, usaremos la ciudad del paciente
    origen_localidad: patient.localidad || "",
    origen_barrio: patient.barrio || "",
    origen_vereda_corregimiento: patient.vereda_corregimiento || "",
    origen_indicaciones: patient.indicaciones || "",
    origen_direccion: patient.direccion || "",
    origen_coordenadas: origen.origen_coordenadas,
    origen_institucion: "", // Por defecto se limpia
  };
}

// ------------------
// EditAuthorizationForm Component
// ------------------
const EditAuthorizationForm = ({
  handleClose,
  fetchAuthorizations,
  authorization,
}) => {
  // Definimos que estamos en modo edición para evitar que se sobreescriban datos
  const isEditMode = true;

  const { user } = useUser();

  // Función para verificar permisos de autorizaciones
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.es_administrador && user.sinRol) return true;
    return user.modulosPermisos?.some(
      (mp) => mp.modulo?.toLowerCase() === "autorizaciones" && mp.permiso === permission
    );
  };

  // Estado del formulario
  const [form, setForm] = useState(initialFormState);
  const [geografia, setGeografia] = useState(null);
  const [patients, setPatients] = useState([]);
  const [cups, setCups] = useState([]);
  const [propositos, setPropositos] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [errors, setErrors] = useState({
    codigo_mipres: "",
    numero_autorizacion: "",
    numero_pre_autorizacion: "",
  });
  const [isMipresExisting, setIsMipresExisting] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [ciudadesOrigen, setCiudadesOrigen] = useState([]);
  const [ciudadesDestino, setCiudadesDestino] = useState([]);
  const [datosOrigenPaciente, setDatosOrigenPaciente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copagoFieldsDisabled, setCopagoFieldsDisabled] = useState(false);
  const [availableEntregas, setAvailableEntregas] = useState([]);
  const [actualizarTraslados, setActualizarTraslados] = useState(true);
  const [trasladosCount, setTrasladosCount] = useState(null);
  const [loadingTrasladosCount, setLoadingTrasladosCount] = useState(false);
  const [authorizationData, setAuthorizationData] = useState(null); // Estado para la autorización completa
  const [trasladosDateLimits, setTrasladosDateLimits] = useState(null); // Estado para las fechas límite de traslados

  // Estado para clientes TAI
  const [clientesTai, setClientesTai] = useState([]);
  const [loadingClientesTai, setLoadingClientesTai] = useState(false);
  const [empresaOriginalPaciente, setEmpresaOriginalPaciente] = useState("");

  // Función para contar traslados asociados (definida antes del useEffect)
  const fetchTrasladosCount = async (authId) => {
    if (!authId) return;
    
    setLoadingTrasladosCount(true);
    try {
      const response = await api.get(`/autorizaciones/${authId}/count-traslados`);
      setTrasladosCount(response.data.count);
    } catch (error) {
      console.error("Error al contar traslados:", error);
      message.error("Error al contar traslados");
      setTrasladosCount(0);
    } finally {
      setLoadingTrasladosCount(false);
    }
  };

  // Al montar, si tenemos una autorización (modo edición), pre-cargar el formulario
  useEffect(() => {
    if (authorization && authorization.id) {
      // Obtener los datos completos de la autorización incluyendo datos del paciente
      const fetchAuthorizationDetails = async () => {
        try {
          const response = await api.get(`/autorizaciones/${authorization.id}`);
          const fullAuthorization = response.data;
          
          // Guardar la autorización completa en el estado
          setAuthorizationData(fullAuthorization);
          
          // Convertir fechas a formato YYYY-MM-DD
          const inicio = fullAuthorization.inicio_fecha_autorizacion
            ? dayjs(fullAuthorization.inicio_fecha_autorizacion).format("YYYY-MM-DD")
            : null;
          const finalF = fullAuthorization.final_fecha_autorizacion
            ? dayjs(fullAuthorization.final_fecha_autorizacion).format("YYYY-MM-DD")
            : null;
          
          // Parsear calendario
          const parsedCal = Array.isArray(fullAuthorization.calendario)
            ? fullAuthorization.calendario
            : typeof fullAuthorization.calendario === "string"
            ? JSON.parse(fullAuthorization.calendario)
            : [];
          setSelectedDays(
            (parsedCal || []).map((d) => dayjs(d).format("YYYY-MM-DD"))
          );
          setDateRange([inicio ? dayjs(inicio) : null, finalF ? dayjs(finalF) : null]);
          setCalendarStartDate(inicio ? dayjs(inicio) : null);

          // Parsear tai_codigo de string a array si es necesario
          let taiCodigoArray = [];
          if (fullAuthorization.tai_codigo) {
            if (Array.isArray(fullAuthorization.tai_codigo)) {
              taiCodigoArray = fullAuthorization.tai_codigo;
            } else if (typeof fullAuthorization.tai_codigo === "string") {
              taiCodigoArray = fullAuthorization.tai_codigo.split(",").map((c) => c.trim()).filter((c) => c);
            }
          }

          // Calcular si tiene Intermunicipal basado en tipo_traslado
          // Si es "Mixto", podría contener Intermunicipal, así que lo mostramos por seguridad
          const tipoTraslado = fullAuthorization.tipo_traslado?.toLowerCase() || "";
          const tieneIntermunicipalCalc = tipoTraslado === "intermunicipal" || tipoTraslado === "mixto";

          // Carga directamente los datos de la autorización con todos los campos
          setForm({
            ...fullAuthorization,
            inicio_fecha_autorizacion: inicio,
            final_fecha_autorizacion: finalF,
            calendario: parsedCal,
            origen_coordenadas: fullAuthorization.origen_coordenadas
              ? parsePoint(fullAuthorization.origen_coordenadas)
              : null,
            destino_coordenadas: fullAuthorization.destino_coordenadas
              ? parsePoint(fullAuthorization.destino_coordenadas)
              : null,
            // Fallback: si origen_ciudad está vacío, usar la ciudad del paciente
            origen_ciudad:
              fullAuthorization.origen_ciudad || fullAuthorization.ciudad || "",
            destino_ciudad: fullAuthorization.destino_ciudad || "",
            mipres_id: fullAuthorization.mipres_id || null,
            // TAI: convertir tai_codigo a array y calcular tiene_intermunicipal
            tai_codigo: taiCodigoArray,
            tiene_intermunicipal: tieneIntermunicipalCalc,
          });
          
          // En modo edición, solo necesitamos el paciente actual en la lista
          if (fullAuthorization.paciente) {
            setPatients([{
              id: fullAuthorization.paciente.id,
              nombre: fullAuthorization.paciente.nombre,
              numero_documento: fullAuthorization.paciente.numero_documento
            }]);

            // Cargar CUPS si el paciente tiene EPS
            if (fullAuthorization.paciente.eps) {
              fetchCupsByCliente(fullAuthorization.paciente.eps);
            }

            // Guardar la empresa original del paciente para modo TAI
            const empresaPaciente = fullAuthorization.paciente.Cliente?.nombre_cliente ||
                                    fullAuthorization.empresa || "";
            setEmpresaOriginalPaciente(empresaPaciente);
          }

          // Si es autorización TAI, cargar clientes TAI basados en la EPS del paciente
          if (fullAuthorization.es_tai) {
            const epsId = fullAuthorization.paciente?.eps || null;
            fetchClientesTai(epsId);
          }

          // Cargar fechas límite de traslados para restricciones de edición
          fetchTrasladosDateLimits();
          
          // Cargar conteo de traslados ya que el switch está activo por defecto
          fetchTrasladosCount(authorization.id);
        } catch (error) {
          console.error("Error al cargar detalles de la autorización:", error);
          message.error("Error al cargar los datos completos de la autorización");
        }
      };
      
      fetchAuthorizationDetails();
    }
  }, [authorization]);

  // Funciones para cargar datos auxiliares
  const fetchDepartamentos = async () => {
    try {
      const response = await api.get("/departamentos");
      setDepartamentos(response.data);
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
      message.error("Error al cargar departamentos.");
    }
  };

  const fetchInstituciones = async () => {
    try {
      const response = await api.get("/instituciones");
      setInstituciones(response.data);
    } catch (error) {
      console.error("Error al cargar instituciones:", error);
      message.error("Error al cargar instituciones.");
    }
  };


  const fetchPropositos = async () => {
    try {
      const response = await api.get("/propositos");
      setPropositos(response.data);
    } catch (error) {
      console.error("Error al cargar propósitos:", error);
      message.error("Error al cargar propósitos.");
    }
  };

  const fetchCiudades = async (departamentoId, setCiudadesFn) => {
    if (!departamentoId) return;
    try {
      const response = await api.get(
        `/ciudades?departamento_id=${departamentoId}`
      );
      setCiudadesFn(response.data);
    } catch (error) {
      console.error("Error al cargar ciudades:", error);
      message.error("Error al cargar ciudades.");
    }
  };

  const fetchCupsByCliente = async (clienteId) => {
    try {
      const response = await api.get(`/cups/cliente/${clienteId}`);
      setCups(response.data);
    } catch (error) {
      console.error("Error al cargar CUPS:", error);
      message.error("Error al cargar CUPS.");
    }
  };

  // Cargar clientes TAI
  const fetchClientesTai = async (epsId = null) => {
    try {
      setLoadingClientesTai(true);
      // Si hay un EPS ID, obtener solo los clientes TAI relacionados a esa EPS
      const endpoint = epsId ? `/clientes/tai/by-eps/${epsId}` : "/clientes/tai";
      const resp = await api.get(endpoint);
      setClientesTai(resp.data);
    } catch (err) {
      console.error("Error al cargar clientes TAI:", err);
      message.error("Error al cargar clientes TAI.");
    } finally {
      setLoadingClientesTai(false);
    }
  };

  // Cargar datos auxiliares al montar
  useEffect(() => {
    fetchDepartamentos();
    fetchInstituciones();
    // No cargar todos los pacientes en modo edición ya que el campo está deshabilitado
    // y ya tenemos la información del paciente desde la autorización
    // fetchPatients();
    fetchPropositos();
  }, []);

  // Cuando ya se tienen los departamentos e instituciones, convertir campos de texto a IDs (si aplica)
  useEffect(() => {
    if (authorization && departamentos.length && instituciones.length) {
      setForm((prev) => {
        
        return {
          ...prev,
          origen_departamento: findDepartamentoIdByName(
            authorization.origen_departamento,
            departamentos
          ),
          destino_departamento: findDepartamentoIdByName(
            authorization.destino_departamento,
            departamentos
          ),
          origen_institucion: findInstitucionIdByName(
            authorization.origen_institucion,
            instituciones
          ),
          destino_institucion: findInstitucionIdByName(
            authorization.destino_institucion,
            instituciones
          ),
          // Preservar las fechas ya convertidas si existen, si no usar las del authorization
          inicio_fecha_autorizacion: prev.inicio_fecha_autorizacion || (
            authorization.inicio_fecha_autorizacion
              ? dayjs(authorization.inicio_fecha_autorizacion).format("YYYY-MM-DD")
              : null
          ),
          final_fecha_autorizacion: prev.final_fecha_autorizacion || (
            authorization.final_fecha_autorizacion
              ? dayjs(authorization.final_fecha_autorizacion).format("YYYY-MM-DD")
              : null
          ),
        };
      });
    }
  }, [authorization, departamentos, instituciones]);

  // <<-- Cargar ciudades si ya existen en el formulario -->
  useEffect(() => {
    if (form.origen_departamento) {
      fetchCiudades(form.origen_departamento, setCiudadesOrigen);
    }
  }, [form.origen_departamento]);

  useEffect(() => {
    if (form.destino_departamento) {
      fetchCiudades(form.destino_departamento, setCiudadesDestino);
    }
  }, [form.destino_departamento]);
  // <<-- Fin de carga de ciudades -->

  // Calcular km por servicios
  const calculateKilometrosPorServicios = () => {
    const { total_traslados_asignados, kilometros_autorizados, tipo_viaje } =
      form;
    if (
      total_traslados_asignados !== undefined &&
      total_traslados_asignados !== null &&
      kilometros_autorizados !== undefined &&
      kilometros_autorizados !== null &&
      tipo_viaje &&
      total_traslados_asignados > 0
    ) {
      const isRedondo = tipo_viaje.toLowerCase() === "redondo";
      const kmPorServicio = isRedondo
        ? kilometros_autorizados / (total_traslados_asignados * 2)
        : kilometros_autorizados / total_traslados_asignados;

      setForm((prev) => ({
        ...prev,
        kilometros_por_servicios: parseFloat(kmPorServicio.toFixed(2)),
      }));
    } else if (kilometros_autorizados === 0 || total_traslados_asignados === 0) {
      // Si alguno es 0, el resultado debe ser 0
      setForm((prev) => ({
        ...prev,
        kilometros_por_servicios: 0,
      }));
    }
  };

  // useEffect para calcular km por servicios cuando cambian los valores relacionados
  useEffect(() => {
    calculateKilometrosPorServicios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.total_traslados_asignados,
    form.kilometros_autorizados,
    form.tipo_viaje,
  ]);

  // Calcular copago
  const calculateCopago = () => {
    if (!copagoFieldsDisabled) return; // Solo se calcula si la lógica lo requiere
    const selectedCups = cups.find((c) => c.codigo === form.cups_codigo);
    if (!selectedCups) return;

    const { codigo } = selectedCups;
    const porcentaje = parseFloat(form.porcentaje_copago_selected) || 0;
    const total = parseInt(form.total_traslados_asignados, 10) || 1;
    const valorMax = parseFloat(form.valor_maximo_copago) || null;

    let copagoTotal = 0;
    let copagoDiario = 0;

    // Si es un cups especial
    if (SPECIAL_CUPS_CODES.includes(codigo)) {
      const valorTraslado = parseFloat(selectedCups.valor_traslado) || 0;
      copagoTotal = valorTraslado * (porcentaje / 100) * total;
      copagoDiario = valorTraslado * (porcentaje / 100);

      if (valorMax && copagoTotal > valorMax) {
        copagoTotal = valorMax;
        copagoDiario = copagoTotal / total;
      }
    } else if (INTERMUNICIPAL_CODES.includes(codigo)) {
      const kmMax = parseFloat(selectedCups.kilometraje_maximo) || 0;
      const valorKm = 3600; // Ejemplo
      copagoTotal = kmMax * valorKm * (porcentaje / 100) * total;
      copagoDiario = kmMax * valorKm * (porcentaje / 100);

      if (valorMax && copagoTotal > valorMax) {
        copagoTotal = valorMax;
        copagoDiario = copagoTotal / total;
      }
    } else {
      // Para otros cups
      copagoTotal = form.copago;
      copagoDiario = copagoTotal / total;
    }

    setForm((prev) => ({
      ...prev,
      copago: parseFloat(copagoTotal.toFixed(2)),
      copago_diario: parseFloat(copagoDiario.toFixed(2)),
    }));
  };

  // useEffect para calcular copago cuando cambian los valores relacionados
  useEffect(() => {
    calculateCopago();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.cups_codigo,
    form.porcentaje_copago_selected,
    form.total_traslados_asignados,
    form.valor_maximo_copago,
    copagoFieldsDisabled,
    cups,
  ]);

  // Intermunicipal => recalcular kmAut cuando cambian los valores relacionados
  useEffect(() => {
    if (INTERMUNICIPAL_CODES.includes(form.cups_codigo)) {
      const total = parseInt(form.total_traslados_asignados, 10) || 1;
      const kmMax = parseFloat(form.kilometraje_maximo) || 0;
      const kmAut = total * 2 * kmMax;
      setForm((prev) => ({
        ...prev,
        kilometros_autorizados: kmAut,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.total_traslados_asignados,
    form.cups_codigo,
    form.kilometraje_maximo,
  ]);

  // Handler onChange general
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Validar copago_diario <= copago
    if (name === "copago" || name === "copago_diario") {
      const copagoVal = parseFloat(name === "copago" ? value : form.copago) || 0;
      const copagoDiarioVal = parseFloat(name === "copago_diario" ? value : form.copago_diario) || 0;
      if (copagoDiarioVal > copagoVal) {
        setErrors((prev) => ({ ...prev, copago_diario: "El Copago por Servicios no puede ser mayor al Copago Total." }));
      } else {
        setErrors((prev) => ({ ...prev, copago_diario: "" }));
      }
    }
  };

  // Otros handlers específicos
  const handleNoPbsChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      no_pbs: checked,
      codigo_mipres: checked ? "" : prev.codigo_mipres,
      numero_entrega: 1,
      crear_nuevo_mipres: false,
      entregas_autorizadas: checked ? 1 : prev.entregas_autorizadas,
    }));
    if (checked) {
      setErrors((prev) => ({ ...prev, codigo_mipres: "" }));
      setIsMipresExisting(false);
      setAvailableEntregas([]);
    }
  };

  // TAI Switch handler
  const handleTaiChange = (checked) => {
    if (checked) {
      // Al activar TAI:
      // 1. Guardar la empresa original del paciente
      setEmpresaOriginalPaciente(form.empresa || "");
      // 2. Limpiar la empresa y CUPS para que seleccione una de las TAI
      // TAI siempre es NO PBS y tipo autorización Cerrada
      setForm((prev) => ({
        ...prev,
        es_tai: true,
        no_pbs: true, // TAI siempre es NO PBS
        tipo_autorizacion: "cerrada", // TAI por defecto tipo cerrada
        codigo_mipres: "", // Limpiar MIPRES ya que es NO PBS
        empresa: "", // Limpiar empresa para seleccionar cliente TAI
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        tai_codigo: [],
        coberturas: "",
        transportadora_id: null,
        cliente_tai_id: null,
        tai_tipo_transporte: null,
        tai_configuracion: null,
      }));
      // Limpiar errores de MIPRES
      setErrors((prev) => ({ ...prev, codigo_mipres: "" }));
      setIsMipresExisting(false);
      setAvailableEntregas([]);
      // 3. Limpiar CUPS (se cargarán al seleccionar cliente TAI)
      setCups([]);
      // 4. Cargar clientes TAI basados en la EPS del paciente
      const selectedPatient = patients.find((p) => p.id === form.paciente_id);
      if (selectedPatient && selectedPatient.eps) {
        // Cargar clientes TAI relacionados con la EPS del paciente
        fetchClientesTai(selectedPatient.eps);
      } else {
        // Si no hay paciente o no tiene EPS, cargar todos los clientes TAI
        fetchClientesTai();
      }
    } else {
      // Al desactivar TAI:
      // 1. Restaurar la empresa original del paciente
      setForm((prev) => ({
        ...prev,
        es_tai: false,
        empresa: empresaOriginalPaciente || prev.empresa,
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        tai_codigo: [],
        coberturas: "",
        transportadora_id: null,
        cliente_tai_id: null,
        tai_tipo_transporte: null,
        tai_configuracion: null,
      }));
      // 2. Recargar CUPS del paciente original
      if (form.paciente_id) {
        const selectedPatient = patients.find((p) => p.id === form.paciente_id);
        if (selectedPatient && selectedPatient.eps) {
          fetchCupsByCliente(selectedPatient.eps);
        }
      }
    }
  };

  // Handler para cambio de empresa TAI
  const handleEmpresaTaiChange = (value) => {
    // Buscar el cliente TAI por nombre_cliente para obtener su ID
    const clienteTai = clientesTai.find((c) => c.nombre_cliente === value);

    // Limpiar CUPS actuales y cargar los del cliente TAI
    setForm((prev) => ({
      ...prev,
      empresa: value,
      cliente_tai_id: clienteTai?.id || null, // Guardar el ID del cliente TAI
      cups_codigo: "",
      cups_descripcion: "",
      tipo_viaje: "",
      tipo_traslado: "",
      tai_codigo: [],
      coberturas: "",
    }));

    // Cargar CUPS del cliente TAI seleccionado
    if (clienteTai && clienteTai.id) {
      fetchCupsByCliente(clienteTai.id);
    } else {
      setCups([]);
    }
  };

  // Handler para selección múltiple de CUPS TAI
  const handleTaiCodigoChange = (selectedCodes) => {
    // Obtener los CUPS seleccionados
    const selectedCups = cups.filter((c) => selectedCodes.includes(c.codigo));

    // Calcular coberturas (separadas por coma)
    const coberturas = selectedCups
      .map((c) => c.cobertura)
      .filter((c) => c)
      .join(", ");

    // Calcular tipo de traslado automáticamente
    const tiposTraslado = [...new Set(selectedCups.map((c) => c.tipo_traslado).filter((t) => t))];
    let tipoTrasladoCalculado = "";

    if (tiposTraslado.length === 1) {
      tipoTrasladoCalculado = tiposTraslado[0];
    } else if (tiposTraslado.length > 1) {
      tipoTrasladoCalculado = "Mixto";
    }

    // Verificar si algún CUPS seleccionado es Intermunicipal
    const tieneIntermunicipal = selectedCups.some(
      (c) => c.tipo_traslado?.toLowerCase() === "intermunicipal"
    );

    // Tipo de viaje: tomar del primer CUPS seleccionado (solo hay Sencillo o Redondo)
    const tipoViajeCalculado = selectedCups.length > 0 ? (selectedCups[0].tipo_viaje || "") : "";

    setForm((prev) => ({
      ...prev,
      tai_codigo: selectedCodes,
      coberturas: coberturas,
      tipo_traslado: tipoTrasladoCalculado,
      tipo_viaje: tipoViajeCalculado,
      // También actualizar cups_codigo y cups_descripcion para compatibilidad
      cups_codigo: selectedCodes.join(", "),
      cups_descripcion: selectedCups.map((c) => c.descripcion).join(" | "),
      // Indicar si hay algún CUPS Intermunicipal seleccionado
      tiene_intermunicipal: tieneIntermunicipal,
    }));
  };

  // TAI Transportadora change
  const handleTransportadoraChange = (value) => {
    setForm((prev) => ({ ...prev, transportadora_id: value }));
  };

  // TAI Con Acompañante change
  const handleConAcompananteChange = (checked) => {
    setForm((prev) => ({ ...prev, con_acompanante: checked }));
  };

  // En modo edición no se actualizan los datos del paciente
  const handlePatientChange = async (value) => {
    if (isEditMode) {
      return;
    }
    const selectedPatient = patients.find((p) => p.id === value);
    if (!selectedPatient) {
      setForm((prev) => ({ ...prev, paciente_id: "" }));
      return;
    }
    const originData = getDatosOrigenPaciente(selectedPatient);
    setDatosOrigenPaciente(originData);
    await fetchCiudades(selectedPatient.departamento_id, setCiudadesOrigen);
    const pForm = getPatientFormData(selectedPatient);
    setForm((prev) => ({ ...prev, ...pForm }));
    if (selectedPatient.eps) {
      fetchCupsByCliente(selectedPatient.eps);
    } else {
      setCups([]);
    }
  };

  const handleCupsChange = (value) => {
    const selected = cups.find((c) => c.codigo === value);
    if (!selected) {
      setForm((prev) => ({
        ...prev,
        cups_codigo: "",
        cups_descripcion: "",
        tipo_viaje: "",
        tipo_traslado: "",
        porcentaje_copago_selected: null,
        copago: 0,
        copago_diario: 0,
        valor_maximo_copago: null,
        kilometros_autorizados: "",
      }));
      setCopagoFieldsDisabled(false);
      return;
    }
    setForm((prev) => ({
      ...prev,
      cups_codigo: selected.codigo,
      cups_descripcion: selected.descripcion,
      tipo_viaje: selected.tipo_viaje,
      tipo_traslado: selected.tipo_traslado,
      valor_traslado: selected.valor_traslado || 0,
      kilometraje_maximo: selected.kilometraje_maximo || null,
    }));
    if (
      SPECIAL_CUPS_CODES.includes(selected.codigo) ||
      INTERMUNICIPAL_CODES.includes(selected.codigo)
    ) {
      setCopagoFieldsDisabled(true);
      setForm((prev) => ({ ...prev, porcentaje_copago_selected: 0 }));
    } else {
      setCopagoFieldsDisabled(false);
      setForm((prev) => ({
        ...prev,
        porcentaje_copago_selected: null,
        copago: 0,
        copago_diario: 0,
        valor_maximo_copago: null,
      }));
    }
    if (INTERMUNICIPAL_CODES.includes(selected.codigo)) {
      const total = parseInt(form.total_traslados_asignados, 10) || 1;
      const kmMax = parseFloat(selected.kilometraje_maximo) || 0;
      const kmAut = total * 2 * kmMax;
      setForm((prev) => ({ ...prev, kilometros_autorizados: kmAut }));
    }
  };

  const handlePropositoChange = (value) => {
    const sel = propositos.find((p) => p.descripcion === value);
    if (!sel) {
      setForm((prev) => ({ ...prev, proposito_id: "", proposito: "" }));
    } else {
      setForm((prev) => ({
        ...prev,
        proposito_id: sel.id,
        proposito: sel.descripcion,
      }));
    }
  };

  const fetchInstitucion = async (id) => {
    const response = await api.get(`/instituciones/${id}`);
    return response.data;
  };

  const updateFormWithInstitucionData = (institucion, type) => {
    const {
      departamento_id,
      ciudad_id,
      localidad,
      barrio,
      direccion,
      coordenadas,
      indicaciones,
    } = institucion;
    
    // Manejar coordenadas de manera segura
    let coords = "";
    if (coordenadas) {
      if (typeof coordenadas === 'string') {
        coords = coordenadas; // Ya está en formato string
      } else if (coordenadas.coordinates && Array.isArray(coordenadas.coordinates) && coordenadas.coordinates.length >= 2) {
        // Formato GeoJSON [lng, lat], convertir a "lat, lng"
        coords = `${coordenadas.coordinates[1]}, ${coordenadas.coordinates[0]}`;
      }
    }
    
    setForm((prev) => ({
      ...prev,
      [`${type}_institucion`]: institucion.id,
      [`${type}_departamento`]: departamento_id,
      [`${type}_ciudad`]: ciudad_id,
      [`${type}_localidad`]: localidad || "",
      [`${type}_barrio`]: barrio || "",
      [`${type}_direccion`]: direccion || "",
      [`${type}_coordenadas`]: coords,
      [`${type}_indicaciones`]: indicaciones || "",
    }));
  };


  // Función para obtener fechas límite de traslados
  const fetchTrasladosDateLimits = async () => {
    if (!authorization?.id) return;
    
    try {
      console.log("Cargando fechas límite para autorización:", authorization.id);
      const response = await api.get(`/autorizaciones/${authorization.id}/traslados-date-limits`);
      console.log("Fechas límite recibidas:", response.data);
      setTrasladosDateLimits(response.data);
    } catch (error) {
      console.error("Error al obtener fechas límite de traslados:", error);
      // No mostramos error al usuario porque es información auxiliar
      setTrasladosDateLimits(null);
    }
  };

  // Handler para el toggle de actualizar traslados
  const handleActualizarTrasladosChange = (checked) => {
    setActualizarTraslados(checked);
    if (checked) {
      fetchTrasladosCount(authorization?.id);
    } else {
      setTrasladosCount(null);
    }
  };

  const handleInstitucionChange = async (value, type) => {
    if (!value) {
      if (type === "origen" && datosOrigenPaciente) {
        setForm((prev) => ({
          ...prev,
          origen_institucion: "",
          ...datosOrigenPaciente,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          [`${type}_institucion`]: "",
          [`${type}_departamento`]: "",
          [`${type}_ciudad`]: "",
          [`${type}_localidad`]: "",
          [`${type}_barrio`]: "",
          [`${type}_direccion`]: "",
          [`${type}_coordenadas`]: "",
          [`${type}_indicaciones`]: "",
        }));
      }
      return;
    }
    try {
      const inst = await fetchInstitucion(value);
      await fetchCiudades(
        inst.departamento_id,
        type === "origen" ? setCiudadesOrigen : setCiudadesDestino
      );
      updateFormWithInstitucionData(inst, type);
    } catch (error) {
      console.error("Error al obtener la institución seleccionada:", error);
      message.error("Error al obtener la institución seleccionada.");
    }
  };

  // ---------------------------
  // PREPARAR DATOS PARA ENVIAR (PUT)
  // ---------------------------
  const prepareFormData = () => {
    const {
      codigo_mipres,
      no_pbs,
      numero_autorizacion,
      numero_pre_autorizacion,
      porcentaje_copago_selected,
      kilometros_autorizados,
      kilometros_por_servicios,
      origen_institucion,
      destino_institucion,
      origen_institucion_temporal,
      destino_institucion_temporal,
      crear_nuevo_mipres,
      mipres_id,
      entregas_autorizadas,
      ...rest
    } = form;

    // Si no hay una institución seleccionada, usamos la versión temporal (por ejemplo "Domicilio")
    const preparedOrigenInstitucion = origen_institucion
      ? getInstitutionName(origen_institucion, instituciones)
      : origen_institucion_temporal || null;
    const preparedDestinoInstitucion = destino_institucion
      ? getInstitutionName(destino_institucion, instituciones)
      : destino_institucion_temporal || null;

    const formData = {
      ...rest,
      no_pbs,
      numero_entrega: form.numero_entrega,
      entregas_autorizadas: form.entregas_autorizadas,
      porcentaje_copago_total: porcentaje_copago_selected,
      usuario_id: user.id,
      inicio_fecha_autorizacion: form.inicio_fecha_autorizacion
        ? dayjs(form.inicio_fecha_autorizacion).format("YYYY-MM-DD")
        : null,
      final_fecha_autorizacion: form.final_fecha_autorizacion
        ? dayjs(form.final_fecha_autorizacion).format("YYYY-MM-DD")
        : null,
      calendario: form.calendario,
      ...(codigo_mipres && {codigo_mipres}),
      ...(no_pbs ? {} : { codigo_mipres }),
      // Siempre enviar numero_autorizacion y numero_pre_autorizacion, aunque estén vacíos
      numero_autorizacion: numero_autorizacion || "",
      numero_pre_autorizacion: numero_pre_autorizacion || "",
      ...(kilometros_autorizados !== undefined && kilometros_autorizados !== null ? { kilometros_autorizados } : {}),
      ...(kilometros_por_servicios !== undefined && kilometros_por_servicios !== null ? { kilometros_por_servicios } : {}),
      // Asignamos las instituciones preparadas:
      origen_institucion: preparedOrigenInstitucion,
      destino_institucion: preparedDestinoInstitucion,
      // Además, guardamos las versiones temporales si es que no hay institución seleccionada
      origen_lugar_temporal:
        !origen_institucion && origen_institucion_temporal
          ? origen_institucion_temporal
          : null,
      destino_lugar_temporal:
        !destino_institucion && destino_institucion_temporal
          ? destino_institucion_temporal
          : null,
      ...(mipres_id ? { mipres_id } : {}),
      // Campos TAI
      es_tai: form.es_tai || false,
      tai_codigo: form.es_tai && form.tai_codigo?.length > 0
        ? (Array.isArray(form.tai_codigo) ? form.tai_codigo.join(", ") : form.tai_codigo)
        : null,
      coberturas: form.es_tai ? form.coberturas : null,
      transportadora_id: form.es_tai ? form.transportadora_id : null,
      cliente_tai_id: form.es_tai ? form.cliente_tai_id : null,
      // tai_tipo_transporte usa el tipo_traslado calculado de los CUPS
      tai_tipo_transporte: form.es_tai ? form.tipo_traslado : null,
      tai_configuracion: form.es_tai ? form.tai_configuracion : null,
      // con_acompanante - solo relevante para TAI Intermunicipal
      // El checkbox solo aparece cuando es TAI e Intermunicipal, así que enviamos el valor directo
      con_acompanante: form.es_tai ? (form.con_acompanante || false) : false,
    };

    // Convertir IDs a texto para departamentos y ciudades (para guardar en la BD)
    formData.origen_departamento = getDepartmentName(
      form.origen_departamento,
      departamentos
    );
    let origenCiudad = getCityName(form.origen_ciudad, ciudadesOrigen);
    if (!origenCiudad) {
      origenCiudad = form.ciudad;
    }
    formData.origen_ciudad = origenCiudad;

    let destinoCiudad = getCityName(form.destino_ciudad, ciudadesDestino);
    if (!destinoCiudad) {
      destinoCiudad = authorization.destino_ciudad || "";
    }
    formData.destino_ciudad = destinoCiudad;

    formData.destino_departamento = getDepartmentName(
      form.destino_departamento,
      departamentos
    );

    return formData;
  };

  const validateMipresBeforeSubmit = async (formData) => {
    if (!formData.no_pbs && formData.codigo_mipres && isMipresExisting) {
      try {
        const response = await api.get(
          `/mipres/${formData.codigo_mipres}/entregas-disponibles`
        );
        const { exists, availableEntregas } = response.data;
        if (!exists) {
          setErrors((prev) => ({
            ...prev,
            codigo_mipres: "El código MIPRES no existe.",
          }));
          message.error("El código MIPRES no existe.");
          return false;
        }
        if (availableEntregas.length === 0) {
          setErrors((prev) => ({
            ...prev,
            codigo_mipres: "Se alcanzó el máximo de entregas para este MIPRES.",
          }));
          message.error("Se alcanzó el máximo de entregas para este MIPRES.");
          return false;
        }
      } catch (error) {
        console.error("Error al validar MIPRES antes de enviar:", error);
        message.error("Error al validar MIPRES antes de enviar.");
        return false;
      }
    }
    return true;
  };
  
  // Handler para submit (PUT)
const handleSubmit = async () => {
  if (
    errors.codigo_mipres ||
    errors.numero_autorizacion ||
    errors.numero_pre_autorizacion ||
    errors.copago_diario
  ) {
    message.error("Corrige los errores en los campos.");
    return;
  }
  if (!form.numero_autorizacion && !form.numero_pre_autorizacion) {
    message.error("Se requiere # de autorización o # de pre-autorización.");
    return;
  }
  // Validar copago_diario <= copago
  const copagoVal = parseFloat(form.copago) || 0;
  const copagoDiarioVal = parseFloat(form.copago_diario) || 0;
  if (copagoDiarioVal > copagoVal) {
    setErrors((prev) => ({ ...prev, copago_diario: "El Copago por Servicios no puede ser mayor al Copago Total." }));
    message.error("El Copago por Servicios no puede ser mayor al Copago Total.");
    return;
  }
  try {
    setLoading(true);
    const formData = prepareFormData();
    // Las ubicaciones se resuelven en el servidor desde el ámbito y tipo de lugar elegidos.
    Object.keys(formData).filter(key => key.startsWith('origen_') || key.startsWith('destino_')).forEach(key => delete formData[key]);
    if (geografia) Object.assign(formData, geografia);
    const mipresOk = await validateMipresBeforeSubmit(formData);
    if (!mipresOk) {
      setLoading(false);
      return;
    }

    // SEGUNDO: Actualizar la autorización con todos los campos
    // Asegurarse de que todos los campos necesarios estén incluidos
    if (form.codigo_mipres) {
      formData.codigo_mipres = form.codigo_mipres;
    }
    
    // Asegurarse de que entregas_autorizadas esté incluido
    formData.entregas_autorizadas = form.entregas_autorizadas;

    const response = await guardarAutorizacion(authorization.id, {
      ...formData,
      actualizar_traslados: actualizarTraslados,
      guardar_anteriores: actualizarTraslados,
    });
    message.success(`Autorización actualizada. ${response.data.traslados_actualizados || 0} traslados con números actualizados.`);

    fetchAuthorizations();
    handleClose();
  } catch (error) {
    console.error("Error al actualizar la autorización:", error);
    
    // Mostrar mensaje específico del servidor si está disponible
    const errorMessage = error?.response?.data?.message || "Error al actualizar la autorización.";
    message.error(errorMessage);
  } finally {
    setLoading(false);
  }
};

  // Render del formulario de edición
  return (
    <Form layout="vertical" onFinish={handleSubmit}>
      <Row gutter={24}>
        {/* Columna 1: Información del Paciente, Responsable y Fechas */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <UserAddOutlined style={{ marginRight: 8 }} />
              Información del Paciente
            </Title>
            <PatientInfo
              form={form}
              handleChange={handleChange}
              patients={patients}
              handlePatientChange={handlePatientChange}
              isEditMode={true}
              // Props para modo TAI
              esTai={form.es_tai}
              clientesTai={clientesTai}
              loadingClientesTai={loadingClientesTai}
              onEmpresaTaiChange={handleEmpresaTaiChange}
              empresaOriginalPaciente={empresaOriginalPaciente}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <TeamOutlined style={{ marginRight: 8 }} />
              Información del Responsable
            </Title>
            <ResponsibleInfo form={form} handleChange={handleChange} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CalendarOutlined style={{ marginRight: 8 }} />
              Fechas de la Autorización
            </Title>
            <AuthorizationDates
              form={form}
              handleChange={handleChange}
              handleDaySelection={() => {}}
              selectedDays={selectedDays}
              dateRange={dateRange}
              setDateRange={setDateRange}
              calendarStartDate={calendarStartDate || dayjs()}
              setCalendarStartDate={setCalendarStartDate}
              trasladosDateLimits={trasladosDateLimits} // Pasar las fechas límite
            />
          </div>
        </Col>

        {/* Columna 2: Datos de la Autorización */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SafetyOutlined style={{ marginRight: 8 }} />
              Datos de la Autorización
            </Title>
            
            <AuthorizationData
              form={form}
              handleChange={handleChange}
              cups={cups}
              handleCupsChange={handleCupsChange}
              errors={errors}
              isMipresExisting={isMipresExisting}
              handleNoPbsChange={handleNoPbsChange}
              propositos={propositos}
              handlePropositoChange={handlePropositoChange}
              isEditMode={true}
              copagoFieldsDisabled={copagoFieldsDisabled}
              availableEntregas={availableEntregas}
              actualizarTraslados={actualizarTraslados}
              handleActualizarTrasladosChange={handleActualizarTrasladosChange}
              trasladosCount={trasladosCount}
              loadingTrasladosCount={loadingTrasladosCount}
              authorizationData={authorizationData}
              esTai={form.es_tai}
              handleTaiCodigoChange={handleTaiCodigoChange}
              handleTaiChange={handleTaiChange}
              hasPermissionTai={hasPermission("SWITCH_TAI_A")}
              transportadoraId={form.transportadora_id}
              onTransportadoraChange={handleTransportadoraChange}
              conAcompanante={form.con_acompanante}
              onConAcompananteChange={handleConAcompananteChange}
              tieneIntermunicipal={form.tiene_intermunicipal}
            />
          </div>
        </Col>

        {/* Columna 3: Ubicación/Origen/Destino */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              Origen y destino del traslado
            </Title>
            <GeografiaAutorizacion pacienteId={form.paciente_id} autorizacionId={authorization?.id} onChange={setGeografia} />
          </div>
        </Col>
      </Row>

      {/* Observaciones y TAI */}
      <Row gutter={24}>
        <Col span={16}>
          <div style={{ marginBottom: 24 }}>
            <Title
              level={4}
              style={{
                marginBottom: 16,
                backgroundColor: "#0a2e5c",
                color: "white",
                padding: 8,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FileTextOutlined style={{ marginRight: 8 }} />
              Observaciones
            </Title>
            <Form.Item>
              <TextArea
                name="observaciones"
                value={form.observaciones}
                onChange={handleChange}
                placeholder="Observaciones"
                rows={4}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>

      {/* Botones de Acción */}
      <Form.Item>
        <Row justify="end">
          <Col>
            <Button
              onClick={() => {
                handleClose();
              }}
              style={{
                backgroundColor: "#d32f2f",
                color: "white",
                borderColor: "#d32f2f",
                borderRadius: 4,
                marginRight: 8,
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#b71c1c")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#d32f2f")
              }
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                backgroundColor: "#0a2e5c",
                color: "white",
                borderColor: "#0a2e5c",
                borderRadius: 4,
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#004080")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#0a2e5c")
              }
            >
              Actualizar
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

EditAuthorizationForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  fetchAuthorizations: PropTypes.func.isRequired,
  authorization: PropTypes.object.isRequired,
};

export default EditAuthorizationForm;